import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const SKILLS = [
  { label: 'Technical Analysis', level: 95 },
  { label: 'Price Action Trading', level: 92 },
  { label: 'Crypto Markets', level: 90 },
  { label: 'Indian Stock Market (NSE/BSE)', level: 88 },
  { label: 'Risk & Money Management', level: 94 },
  { label: 'Stress & Psychology Management', level: 85 },
  { label: 'Capital Management', level: 91 },
  { label: 'F&O Strategies', level: 80 },
];

const MILESTONES = [
  { year: '2022', title: 'Started Trading Journey', desc: 'Entered the Indian stock market. Learned the hard way with real capital.' },
  { year: '2023', title: 'Went Deep into Technical Analysis', desc: 'Studied thousands of charts. Mastered candlestick patterns, indicators, and market structure.' },
  { year: '2024', title: 'Entered the Crypto World', desc: 'Rode the 2021 bull market. Studied Bitcoin cycles, altcoin seasons, and DeFi deeply.' },
  { year: '2025', title: 'Started Kalpnic Trading', desc: 'Began teaching others. Launched Kalpnic Trading to share proven strategies with retail traders.' },
  { year: '2026', title: '500+ Students Trained', desc: 'Grew the community to 500+ active traders and investors. Consistent 85%+ accuracy on signals.' },
];

const VALUES = [
  { icon: '🎯', title: 'Accuracy First', desc: 'Every call is backed by thorough analysis. We only share what we trade ourselves.' },
  { icon: '🤝', title: 'Community Driven', desc: 'We grow together. No gatekeeping — we share everything that works in real markets.' },
  { icon: '📚', title: 'Education Always', desc: 'We don\'t just give fish. We teach you to fish. Understanding > Just following signals.' },
  { icon: '💎', title: 'Long-Term Focus', desc: 'Building sustainable wealth, not get-rich-quick schemes. Compounding takes patience.' },
];

const About = () => {
  const WHATSAPP_NUMBER = '919926749937';

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="page-hero section-padding grid-bg">
        <div className="hero-glow-small" />
        <div className="container">
          <div className="tag">About Us</div>
          <h1 className="section-title">The Mind Behind<br /><span className="gold-text">Kalpnic Trading</span></h1>
          <p className="section-subtitle">
            A passionate trader turned educator — on a mission to help every Indian retail investor
            trade smarter, not harder.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="founder-section section-padding">
        <div className="container">
          <div className="founder-grid">
            <div className="founder-visual">
              <div className="founder-avatar-box">
                <div className="founder-avatar">
                  <span className="avatar-initials">TE</span>
                </div>
                <div className="founder-glow" />
                <div className="founder-badges-float">
                  <div className="float-badge badge-1">📈 3+ Years Exp</div>
                  <div className="float-badge badge-2">🇮🇳 NSE/BSE Pro</div>
                  <div className="float-badge badge-3">🪙 Crypto Expert</div>
                  <div className="float-badge badge-4">🎓 500+ Students</div>
                </div>
              </div>
            </div>

            <div className="founder-content">
              <div className="tag">Founder & Head Mentor</div>
              <h2 className="section-title">Your Mentor's <span className="gold-text">Story</span></h2>
              <div className="founder-story">
                <p>
                  My trading journey started back in <strong>2022</strong> when I entered the Indian stock market with very little knowledge and even less patience — and paid the price for it. Like most retail traders, I made every mistake in the book: chasing momentum, ignoring stop losses, over-leveraging.
                </p>
                <p>
                  But instead of quitting, I went all in on <strong>learning</strong>. I spent years studying technical analysis, price action, market structure, and the psychology of trading. Over <strong>3 years in the Indian market</strong> (NSE/BSE equities, F&O), I developed a deep understanding of how domestic markets behave.
                </p>
                <p>
                  In <strong>2024</strong>, I entered the <strong>crypto market</strong> — just in time for the bull run, and more importantly, for the brutal 2025 bear market. Surviving and profiting through a 70% market crash taught me the most valuable lessons about <strong>capital management, risk control, and emotional discipline</strong>.
                </p>
                <p>
                  Today, I combine <strong>2+ years of Crypto expertise</strong> and <strong>3+ years of Indian Market experience</strong> to give students and subscribers a complete market education. Every course, every call, every strategy — it's battle-tested in real markets with real money.
                </p>
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi! I want to connect with the Kalpnic Trading mentor.')}`}
                target="_blank"
                rel="noreferrer"
                className="btn-gold"
              >
                Connect on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="skills-section section-padding">
        <div className="container">
          <div className="skills-grid">
            <div className="skills-content">
              <div className="tag">Expertise</div>
              <h2 className="section-title">Skills &<br /><span className="gold-text">Specializations</span></h2>
              <p className="section-subtitle">
                Years of real-market experience distilled into teachable skills that actually make money.
              </p>
            </div>
            <div className="skills-bars">
              {SKILLS.map((skill, i) => (
                <div className="skill-item" key={i}>
                  <div className="skill-header">
                    <span className="skill-label">{skill.label}</span>
                    <span className="skill-pct">{skill.level}%</span>
                  </div>
                  <div className="skill-bar">
                    <div
                      className="skill-fill"
                      style={{ width: `${skill.level}%`, animationDelay: `${i * 0.1}s` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="timeline-section section-padding">
        <div className="container">
          <div className="section-header">
            <div className="tag">Journey</div>
            <h2 className="section-title">The Road to<br /><span className="gold-text">Kalpnic Trading</span></h2>
          </div>
          <div className="timeline">
            {MILESTONES.map((m, i) => (
              <div className={`timeline-item ${i % 2 === 0 ? 'left' : 'right'}`} key={i}>
                <div className="timeline-content card">
                  <span className="timeline-year">{m.year}</span>
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                </div>
                <div className="timeline-dot" />
              </div>
            ))}
            <div className="timeline-line" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section section-padding">
        <div className="container">
          <div className="section-header">
            <div className="tag">Our Values</div>
            <h2 className="section-title">What We <span className="gold-text">Stand For</span></h2>
          </div>
          <div className="values-grid">
            {VALUES.map((v, i) => (
              <div className="value-card card" key={i}>
                <span className="value-icon">{v.icon}</span>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta section-padding">
        <div className="container">
          <div className="about-cta-inner">
            <h2 className="section-title">Ready to Learn from a<br /><span className="gold-text">Real Trader?</span></h2>
            <p>Join our growing community of traders and investors. Let's build wealth together.</p>
            <div className="cta-buttons">
              <Link to="/courses" className="btn-gold">Browse Courses</Link>
              <Link to="/subscription" className="btn-outline">View Subscriptions</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
